import { docViewerKind, isBareHttpsUrl } from './doc-viewer';
import { parseFileRecordContent } from './file-record';

/** How many lines a thumbnail can show before the card runs out of room. More than this is wasted
 *  parsing, and fewer reads as a fragment. */
export const PREVIEW_LINE_LIMIT = 8;

/** A single line longer than this is cut: a minified JSON file is ONE line of 40k characters, and laying
 *  that out to measure it is the freeze this cap exists to avoid. */
export const PREVIEW_MAX_LINE_LENGTH = 200;

/** Block-level tags whose CLOSE is a line break in the rendered page — so a heading and the paragraph
 *  after it do not run together into one line of preview. */
const BLOCK_BREAK = /<\/(p|div|h[1-6]|li|tr|section|header|footer|article|blockquote|pre|td|th)\s*>|<br\s*\/?>|<\/?hr\s*\/?>/gi;

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
};

/**
 * An html document's visible text: what a reader would see, not the markup.
 *
 * `<script>` and `<style>` bodies are removed ENTIRELY rather than tag-stripped — their contents are code,
 * and a thumbnail full of CSS selectors is worse than the glyph it replaces. Shared 1:1 with iOS and
 * Android `htmlToVisibleText`.
 */
export function htmlToVisibleText(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<script[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<style[\s\S]*?<\/style\s*>/gi, '')
    .replace(BLOCK_BREAK, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&(nbsp|amp|lt|gt|quot|#39|apos);/gi, (m) => ENTITIES[m.toLowerCase()] ?? m)
    // Collapse runs of spaces/tabs but NEVER newlines — those are the line structure the preview needs.
    .replace(/[^\S\n]+/g, ' ');
}

/**
 * The first lines of a text-shaped file, for a card thumbnail that shows what the document SAYS instead of
 * a generic file-type glyph — or null when this file has no previewable text WITHOUT a network fetch.
 *
 * Deliberately no fetching: a gallery is a grid, and a body request per card is exactly the unbounded
 * per-row cost the performance rules forbid. An agent-written artifact stores its body inline, which is the
 * common case here; a file whose content is only a URL keeps its type glyph.
 *
 * Shared 1:1 with iOS `FilePreviewText` and Android `FilePreviewText`.
 */
export function filePreviewLines(
  name: string | undefined,
  content: string | undefined,
  contentType?: string,
  limit: number = PREVIEW_LINE_LIMIT,
): string[] | null {
  const kind = docViewerKind(name, content, contentType);
  // An image/video/wixel card already previews its real asset. `doc`/`slides`/`pdf` are converted or
  // binary, so there is nothing to read off the stored content.
  if (kind !== 'markdown' && kind !== 'text' && kind !== 'csv' && kind !== 'html') return null;
  if (!content) return null;

  const { body: stored } = parseFileRecordContent(content);
  // A body that is only a pointer has nothing to read here.
  if (isBareHttpsUrl(stored)) return null;
  // An html page previews as its VISIBLE TEXT, not its source. A tag soup thumbnail says nothing about
  // the page, and rendering one live would mean a WebView (or an iframe) per card in a grid — the
  // unbounded per-row cost the performance rules forbid. Stripping is what makes it free.
  const body = kind === 'html' ? htmlToVisibleText(stored) : stored;

  // CRLF is ONE break: splitting on each character would put a phantom blank line after every real one,
  // halving what the card can show. Normalised first so all three platforms agree.
  const lines = body.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  let start = 0;
  while (start < lines.length && lines[start].trim() === '') start += 1;

  const kept = lines.slice(start, start + limit).map((line) => {
    const trimmed = line.trim();
    return trimmed.length > PREVIEW_MAX_LINE_LENGTH ? trimmed.slice(0, PREVIEW_MAX_LINE_LENGTH) : trimmed;
  });
  // Trailing blanks are dropped as well as leading ones: a file that ends in newlines would otherwise
  // spend preview rows on nothing. Interior blanks stay — they are the document's own paragraph texture.
  while (kept.length > 0 && kept[kept.length - 1] === '') kept.pop();

  return kept.length > 0 ? kept : null;
}

/**
 * How a thumbnail should RENDER a previewable file: as a real miniature (`markdown`/`html`) or as its
 * lines (`text`).
 *
 * `csv` and plain text stay lines on purpose — a csv's rows and a log's columns ARE its shape, and a
 * markdown renderer would turn a table's pipes into something it is not. Shared with the native cards.
 */
export function previewRichKind(
  name: string | undefined,
  content: string | undefined,
  contentType?: string,
): 'markdown' | 'html' | 'csv' | 'text' {
  const kind = docViewerKind(name, content, contentType);
  if (kind === 'markdown') return 'markdown';
  if (kind === 'html') return 'html';
  // A spreadsheet's SHAPE is its grid, so a `.csv` thumbnail draws a table like every other surface does with
  // it — rendering it as `a,b,c` lines showed the delimiters, which is the raw source, not the document.
  if (kind === 'csv') return 'csv';
  return 'text';
}
